import { useGlobalRegions } from '../../../hooks';
import { GlobalRegion } from '../../../shared/constants';
import { bookingFlowStoreSelector } from '../../../store';
import {
  useIsPayLaterPriceAvailable,
  useIsPayNowPriceAvailable,
} from '../../../store/booking-flow/booking-flow.selectors';
import { preCheckStoreSelector } from '../../../store/pre-check/pre-check.store';

/**
 * Determines whether the discount section should be visible in the Car Info Accordion.
 *
 * The section is hidden (returns false) when ANY of the following is true:
 * - The user is in the APAC region
 * - The selected vehicle is an Avis First vehicle (ARAF-8605)
 *
 * Avis First detection checks whether the SELECTED VEHICLE is AF:
 * 1. `isAvisFirst` flag from booking flow store (set during vehicle selection)
 * 2. PreCheck store subBrand === 'Q' (modification flows)
 * 3. Vehicle code starts with 'Q' (AF vehicle code prefix)
 *
 * NOTE: We intentionally do NOT check pickup location (A1 prefix) or
 * associatedLocation here because AF locations also offer regular vehicles.
 * Those checks would incorrectly suppress discounts for non-AF vehicles
 * booked at AF-capable locations.
 *
 * The section is shown (returns true) only when ALL of the following are met:
 * - Not APAC, not Avis First vehicle
 * - An alternative payment option exists
 * - Every vehicle price entry has a discount value > 0
 *
 * @param vehiclePayType - The currently selected payment type (`'payNow'` | `'payLater'`).
 * @returns `boolean` — `true` if the discount section should be rendered.
 */
export const useShowDiscountSection = (vehiclePayType: string) => {
  const prices = bookingFlowStoreSelector.use.prices();
  const isPayLaterPriceAvailable = useIsPayLaterPriceAvailable();
  const isPayNowPriceAvailable = useIsPayNowPriceAvailable();
  const { belongsToRegions } = useGlobalRegions();

  // --- Avis First vehicle detection (ARAF-8605) ---
  // Primary: store flag set during vehicle selection on VLP
  const isAvisFirstFromStore = bookingFlowStoreSelector.use.isAvisFirst();

  // Fallback 1: PreCheck store for modification flows
  const isAvisFirstFromPreCheck =
    preCheckStoreSelector.use.preCheckInfo()?.reservationInformation
      ?.brandInformation?.subBrand === 'Q';

  // Fallback 2: Vehicle code prefix (AF vehicles use 'Q' prefix)
  const vehicleCode = bookingFlowStoreSelector.use.vehicleCode?.();
  const isAvisFirstFromVehicleCode =
    typeof vehicleCode === 'string' && vehicleCode.startsWith('Q');

  // Combined: vehicle-level signals only
  const isAvisFirst =
    isAvisFirstFromStore ||
    isAvisFirstFromPreCheck ||
    isAvisFirstFromVehicleCode;

  // --- Region check ---
  const isApac = belongsToRegions([GlobalRegion.APAC]);

  // Early exit: hide discount section for APAC or Avis First vehicle
  if (isApac || isAvisFirst) {
    return false;
  }

  // --- Discount eligibility ---
  const hasAlternativePayType =
    (vehiclePayType === 'payNow' && isPayLaterPriceAvailable) ||
    (vehiclePayType === 'payLater' && isPayNowPriceAvailable);

  const allPricesHaveDiscount = prices?.every(
    (price) => Number(price.discount) > 0,
  );

  return hasAlternativePayType && !!allPricesHaveDiscount;
};
